import { useMemo } from "react";
import { useFavoriteStore, useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useTenantInMarketStore } from "..";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import IFavorite, { FavoriteTargetType } from "../interfaces/IFavorite";
import GeometryService from "../services/geometryService";
import useFocusShape from "./useFocusShape";

export default function (favorites: IFavorite[]): IFavorite[] {
    const geometryService = useMemo(() => new GeometryService(), []);
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const favoriteStore = useFavoriteStore();
    const focusShape = useFocusShape();

    return useMemo(() => {
        if (!focusShape)
            return favorites;

        return favorites.filter(f => {
            switch (f.targetType) {
                case FavoriteTargetType.Property: {
                    const favoritePropertyDetails = favoriteStore.favoritePropertiesDetails.find(fpd => fpd.id === f.targetId)!;
                    return favoritePropertyDetails && geometryService.isPointInsideShape(favoritePropertyDetails.centerPoint, focusShape);
                }
                case FavoriteTargetType.Deal: {
                    const deal = pipedriveStore.deals.find(d => d.id === f.targetId);
                    return deal && geometryService.isPointInsideShape(new GeoCoordinate(deal.lat, deal.lon), focusShape);
                }
                case FavoriteTargetType.Lead: {
                    const lead = leadStore.leads.find(l => l.id === f.targetId);
                    return lead && geometryService.isPointInsideShape(lead.centerPoint, focusShape);
                }
                case FavoriteTargetType.LeaseComp: {
                    const leaseComp = leaseCompStore.leaseComps.find(lc => lc.id === f.targetId);
                    return leaseComp && geometryService.isPointInsideShape(leaseComp.centerPoint, focusShape);
                }
                case FavoriteTargetType.SaleComp: {
                    const saleComp = saleCompStore.saleComps.find(sc => sc.id === f.targetId);
                    return saleComp && geometryService.isPointInsideShape(saleComp.centerPoint, focusShape);
                }
                case FavoriteTargetType.TenantInMarket: {
                    const tenantInMarket = tenantInMarketStore.tenantsInMarket.find(tim => tim.id === f.targetId);
                    if (!tenantInMarket)
                        return false;

                    const centerPoint = geometryService.getShapeCenterPoint(tenantInMarket.shape);
                    return geometryService.isPointInsideShape(new GeoCoordinate(centerPoint.lat, centerPoint.lng), focusShape);
                }
                case FavoriteTargetType.SavedView: {
                    return true
                }
                default:
                    return false;
            }
        });
    }, [favorites, focusShape]);
}